import {defineAbilityConfig} from '../system/defineAbilityConfig';
import {ENTITLEMENT_KEYS} from '@packages/domains/billing/constants';

export const canAccessSinglePagePublishing = defineAbilityConfig({
  entitlements: [ENTITLEMENT_KEYS.ALLOW_SINGLE_PAGE_PUBLISHING],
} as const);

// === Layer 1: pure role checks ===

// Full-site publish to custom domains. Pure permission check; consumers
// asking "does this user's role allow full-site publishing?" should use this.
export const canPublishDesign = defineAbilityConfig({
  userTypes: {authorizedInDesigner: true},
  permission: ['publish', 'site'],
  siteModes: {design: true, edit: true},
} as const);

// Full-site publish to the .webflow.io subdomain. Some roles can publish
// subdomains but not custom domains.
export const canPublishSubdomainDesign = defineAbilityConfig({
  userTypes: {authorizedInDesigner: true},
  workflows: {
    canvas: true,
    preview: true,
    logicFlows: true,
    webApps: true,
    optimization: true,
    optimizationRedirectTest: true,
  },
  permission: ['subPublish', 'site'],
} as const);

// Site-level: user's role grants page-publish-to-production on site pages.
export const canPagePublishToProduction = defineAbilityConfig({
  permission: ['sppPublishToProduction', 'sitePage'],
} as const);

// Site-level: user's role grants page-publish-to-staging on site pages.
export const canPagePublishToStaging = defineAbilityConfig({
  permission: ['sppPublishToStaging', 'sitePage'],
} as const);

// === Layer 2: role aggregates (strategy-independent) ===

// User has at least one page-publish role.
export const canPagePublish = defineAbilityConfig([
  {abilities: {canPagePublishToProduction: true}},
  {abilities: {canPagePublishToStaging: true}},
] as const);

// User has at least one full-site publish role. Strategy-independent —
// answers "can this user, in principle, publish the full site?" Consumers
// like the publish toggle's site-segment role tooltip read this directly.
export const canFullSitePublish = defineAbilityConfig([
  {abilities: {canPublishDesign: true}},
  {abilities: {canPublishSubdomainDesign: true}},
] as const);

// === Layer 3: role + page state (still strategy-independent) ===

// User can page-publish the currently active page to production: the role
// check plus the SitePage/sppPublishToProduction authorization for the current
// page — the same check the server enforces. That authorization honors page
// restrictions and locale access but not SitePage/editContent, so roles that
// require approval for page content changes can still publish.
export const canSpecificPagePublishToProduction = defineAbilityConfig({
  abilities: {canPagePublishToProduction: true},
  authorizations: {'SitePage/sppPublishToProduction': true},
} as const);

// Staging counterpart of `canSpecificPagePublishToProduction`.
export const canSpecificPagePublishToStaging = defineAbilityConfig({
  abilities: {canPagePublishToStaging: true},
  authorizations: {'SitePage/sppPublishToStaging': true},
} as const);

// User can page-publish the currently active page to at least one target.
export const canSpecificPagePublish = defineAbilityConfig([
  {abilities: {canSpecificPagePublishToProduction: true}},
  {abilities: {canSpecificPagePublishToStaging: true}},
] as const);

// === Layer 4: strategy-aware composites ===

// Right now, in the user's current publish strategy, can they publish to
// staging? In site strategy this is the full-site subdomain publish role;
// in page strategy this is the page-publish-to-staging role plus the
// per-page PSA gate (`canSpecificPagePublishToStaging`). Consumers gating
// "Publish to staging" UI should use this directly.
export const canPublishToStaging = defineAbilityConfig([
  {
    userTypes: {authorizedInDesigner: true},
    abilities: {canPublishSubdomainDesign: true},
    publish: {strategy: 'site'},
  },
  {
    userTypes: {authorizedInDesigner: true},
    abilities: {canSpecificPagePublishToStaging: true},
    publish: {strategy: 'page'},
  },
] as const);

// Right now, in the user's current publish strategy, can they publish to
// production? In site strategy this is the full-site custom-domain publish
// role; in page strategy this is the page-publish-to-production role plus the
// per-page PSA gate (`canSpecificPagePublishToProduction`). Consumers gating
// "Publish to all domains" UI should use this directly.
export const canPublishToProduction = defineAbilityConfig([
  {
    userTypes: {authorizedInDesigner: true},
    abilities: {canPublishDesign: true},
    publish: {strategy: 'site'},
  },
  {
    userTypes: {authorizedInDesigner: true},
    abilities: {canSpecificPagePublishToProduction: true},
    publish: {strategy: 'page'},
  },
] as const);
